/**
 * Bundle Builder - Green Llama Enhanced
 * Feature-rich bundle builder with configurable tiers, duplicates, and subscriptions
 */

class BundleBuilder {
  constructor(containerSelector = '#bundle-builder') {
    this.container = document.querySelector(containerSelector);

    if (!this.container) {
      console.error('Bundle Builder: Container not found');
      return;
    }

    // Configuration from data attributes
    this.config = {
      minItems: parseInt(this.container.dataset.minItems) || 1,
      maxItems: parseInt(this.container.dataset.maxItems) || 6,
      defaultBundleSize: parseInt(this.container.dataset.defaultBundleSize) || parseInt(this.container.dataset.maxItems) || 4,
      discountTiers: JSON.parse(this.container.dataset.discountTiers || '{}'),
      bundleSizes: JSON.parse(this.container.dataset.bundleSizes || '[]'),
      allowDuplicates: this.container.dataset.allowDuplicates === 'true',
      showBundleSizeSelector: this.container.dataset.showBundleSizeSelector === 'true',
      showSubscriptionOption: this.container.dataset.showSubscriptionOption === 'true',
      subscriptionDiscount: parseInt(this.container.dataset.subscriptionDiscount) || 0,
      showCategoryTracker: this.container.dataset.showCategoryTracker === 'true',
      currency: this.container.dataset.currency || 'GBP',
      addMoreText: 'ADD {count} MORE'
    };

    // Tier boundaries = item counts that unlock a discount, e.g. [2, 4, 6].
    this.config.tierBoundaries = Object.keys(this.config.discountTiers)
      .map(k => parseInt(k, 10))
      .filter(c => (this.config.discountTiers[c.toString()] || 0) > 0)
      .sort((a, b) => a - b);

    // Dynamic mode (size picker hidden): the bundle has no fixed size. It grows
    // to the next tier as items are added, can only be added to cart when the
    // count sits exactly on a tier (e.g. 2, 4, 6), and the discount floors to the
    // last unlocked tier in between.
    this.dynamicMode = !this.config.showBundleSizeSelector && this.config.tierBoundaries.length > 0;

    // Update flow context, decided in Liquid and read here at construction time
    // so it is settled before the first render. Null means the ordinary create
    // flow, which must behave exactly as it always has. Gated on dynamicMode as
    // well as on the markup, because the exact-tier gate only exists in dynamic
    // mode and it is the only thing keeping an off-tier bundle out of the cart.
    this.editing = (this.dynamicMode && window.BundleEditor)
      ? window.BundleEditor.readContext(this.container)
      : null;
    this.editResult = null;

    // State - use defaultBundleSize (first enabled bundle size option)
    this.state = {
      selectedItems: [],
      currentBundleSize: this.dynamicMode ? this.config.tierBoundaries[0] : this.config.defaultBundleSize,
      purchaseType: 'one-time',
      isLoading: false,
      isAddingToCart: false,
      hasBundleInCart: this.container.dataset.hasBundleInCart === 'true'
    };

    // Skew guard. The section is what stamps data-has-bundle-in-cart, so if this
    // script is ever running against an older copy of the section the attribute
    // is absent and the line above silently resolves to false, which would drop
    // the one-bundle lock entirely. Absent is not the same as "no bundle": fall
    // back to asking the cart. Present means trust it and do not fetch.
    this.needsCartProbe = this.container.dataset.hasBundleInCart === undefined;
    this._renderedSize = null;

    // Cache DOM elements
    this.cacheElements();

    // Currency formatter
    this.currencyFormatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: this.config.currency
    });

    this.init();
  }

  /**
   * Cache all DOM elements
   */
  cacheElements() {
    this.elements = {
      productCards: this.container.querySelectorAll('.bundle-product-card'),
      sidebar: document.getElementById('bundle-sidebar'),
      countDisplay: document.getElementById('bundle-count'),
      bundleMaxDisplay: document.getElementById('bundle-max'),
      productsCountDisplay: document.getElementById('products-count'),
      maxItemsDisplay: document.getElementById('max-items-display'),
      selectedItemsContainer: document.getElementById('selected-items'),
      itemsGrid: document.getElementById('items-grid'),
      showToggle: document.getElementById('show-toggle'),
      addMoreBtn: document.getElementById('add-more-btn'),
      addMoreText: document.getElementById('add-more-text'),
      discountCelebration: document.getElementById('discount-celebration'),
      celebrationText: document.getElementById('celebration-text'),
      subtotalDisplay: document.getElementById('bundle-subtotal'),
      discountDisplay: document.getElementById('bundle-discount'),
      discountRow: document.getElementById('discount-row'),
      subscriptionDiscountDisplay: document.getElementById('bundle-subscription-discount'),
      subscriptionRow: document.getElementById('subscription-row'),
      totalDisplay: document.getElementById('bundle-total'),
      addToCartBtn: document.getElementById('add-bundle-to-cart'),
      submitBtnText: document.getElementById('submit-btn-text'),
      submitBtnPriceWas: document.getElementById('submit-btn-price-was'),
      submitBtnPriceNow: document.getElementById('submit-btn-price-now'),
      progressFill: document.getElementById('progress-indicator-fill'),
      currentMarker: document.getElementById('current-marker'),
      progressIndicator: this.container.querySelector('.bundle-progress-indicator'),
      sidebarToggle: document.getElementById('sidebar-toggle'),
      bundleSizeOptions: this.container.querySelectorAll('.bundle-size-selector__option'),
      frequencyOptions: document.querySelectorAll('.bundle-sidebar__frequency-radio'),
      oneTimePrice: document.getElementById('one-time-price'),
      subscriptionPrice: document.getElementById('subscription-price'),
      oneTimeCompare: document.getElementById('one-time-compare'),
      subscriptionCompare: document.getElementById('subscription-compare'),
      categoryTracker: document.getElementById('category-tracker'),
      categoryList: document.getElementById('category-list'),
      discountTiersInfo: document.getElementById('discount-tiers-info'),
      tierItems: document.querySelectorAll('.bundle-sidebar__tier'),
      sidebarHeader: document.querySelector('.bundle-sidebar__header'),
      expandHint: document.getElementById('expand-hint'),
      savingsBlock: document.getElementById('bundle-savings'),
      savingsOriginal: document.getElementById('savings-original'),
      savingsNow: document.getElementById('savings-now'),
      savingsBadge: document.getElementById('savings-badge'),
      savingsNext: document.getElementById('savings-next')
    };

    // Track grid expanded state
    this.gridExpanded = false;

    // Track previous discount rate for celebration
    this.previousDiscountRate = 0;
  }

  /**
   * Initialize event listeners and initial state
   */
  init() {
    // Product card interactions
    this.elements.productCards.forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.bundle-product-card__variant-select') ||
            e.target.closest('.bundle-product-card__qty-btn') ||
            e.target.closest('.bundle-product-card__quick-remove')) {
          return;
        }
        this.handleProductClick(card);
      });

      const variantSelect = card.querySelector('.bundle-product-card__variant-select');
      variantSelect?.addEventListener('change', (e) => {
        e.stopPropagation();
        this.handleVariantChange(card, e);
      });

      if (this.config.allowDuplicates) {
        const minusBtn = card.querySelector('.bundle-product-card__qty-btn--minus');
        const plusBtn = card.querySelector('.bundle-product-card__qty-btn--plus');

        minusBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          this.handleQuantityChange(card, -1);
        });

        plusBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          const vs = card.querySelector('.bundle-product-card__variant-select');
          const vrs = JSON.parse(card.dataset.variants);
          const cv = vs ? vrs.find(v => v.id === parseInt(vs.value)) : vrs[0];
          if (cv && !cv.available) {
            this.showToast(`${card.dataset.productTitle} is currently sold out`);
            return;
          }
          this.handleQuantityChange(card, 1);
        });
      }

      const removeBtn = card.querySelector('.bundle-product-card__quick-remove');
      removeBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeProductFromBundle(card.dataset.productId);
      });
    });

    // Bundle size selector
    this.elements.bundleSizeOptions.forEach(option => {
      option.addEventListener('click', () => this.handleBundleSizeChange(option));
    });

    // Purchase frequency selector
    this.elements.frequencyOptions.forEach(option => {
      option.addEventListener('change', (e) => this.handleFrequencyChange(e));
    });

    // Add to cart handler
    this.elements.addToCartBtn?.addEventListener('click', () => this.handleAddToCart());

    // Mobile sidebar toggle - make entire header area tappable
    this.elements.sidebarToggle?.addEventListener('click', () => this.toggleSidebar());
    this.elements.sidebarHeader?.addEventListener('click', () => this.toggleSidebar());
    this.elements.expandHint?.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent double trigger from header click
      this.toggleSidebar();
    });

    // Show toggle for items grid
    this.elements.showToggle?.addEventListener('click', () => this.toggleGridExpand());

    // Add more button - scrolls to product grid
    this.elements.addMoreBtn?.addEventListener('click', () => this.scrollToProducts());

    // Starter bundle presets — click to pre-fill the builder
    this.container.querySelectorAll('.bundle-presets__card').forEach(btn => {
      btn.addEventListener('click', () => {
        let products = [];
        try { products = JSON.parse(btn.dataset.presetProducts || '[]'); } catch (e) { products = []; }
        const size = parseInt(btn.dataset.presetSize) || products.length;
        this.applyPreset(products, size);
      });
    });

    // Update flow: rebuild the selection from the cart bundle before the first
    // render, so the customer never sees an empty builder flash and then fill.
    // Runs ahead of render() and ahead of parsePrefill() deliberately.
    this.setupEditMode();

    // Initial render
    this.render();

    // Funnel: builder viewed
    this.track('bundle_builder_viewed', { max_items: this.config.maxItems });

    // Process prefill from URL (must run after render so DOM is ready).
    // Ignored in update mode: the cart bundle is the source of truth there, and
    // a prefill link layered on top produces a replace-or-keep prompt whose
    // "replace" cannot do anything, because removePrefillOriginals skips lines
    // carrying _bundle_id.
    if (this.editing) {
      this.clearPrefillParam();
    } else {
      this.parsePrefill();
    }

    console.log('Bundle Builder Enhanced initialized', this.config);
  }

  /**
   * Enter the update flow when the section handed us an editing context.
   *
   * Whether a bundle exists in the cart is already known from markup, so unlike
   * the fetch this replaces there is nothing here that can fail open and let a
   * second bundle through.
   *
   * If the bundle cannot be fully reconstructed we deliberately fall back to
   * the lock. Showing five of someone's six items with no explanation reads as
   * the site losing their work; an honest wall does not.
   */
  setupEditMode() {
    // Only reachable when the section is older than this script, see the skew
    // guard in the constructor. Restores the previous fetch-based lock so the
    // one-bundle rule still holds while the two are out of step.
    if (this.needsCartProbe) {
      fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(res => res.json())
        .then(cart => {
          this.state.hasBundleInCart = cart.items.some(
            item => item.properties && item.properties._bundle_id
          );
          this.render();
        })
        .catch(() => {
          // Leave the builder usable rather than stranding the customer.
        });
      return;
    }

    if (!this.editing || !window.BundleEditor) return;

    const result = window.BundleEditor.hydrate(this, this.editing);
    this.editResult = result;

    if (!result.ok) {
      this.editing = null;
      this.state.selectedItems = [];
      this.state.hasBundleInCart = true;
      return;
    }

    // The bundle is represented in the builder now, so the lock has no job.
    this.state.hasBundleInCart = false;

    // Baseline for "has anything actually changed", and for naming a tier move.
    this.editOriginalSignature = window.BundleEditor.signature(this.state.selectedItems);
    this.editOriginalRate = this.calculateTotals().discountRate;

    // A flat snapshot, not a reference: the item objects are mutated in place as
    // the customer edits, and this has to still describe what is in their cart
    // so we can put it back if a recovery attempt fails.
    this.editOriginalItems = this.state.selectedItems.map(item => ({
      variantId: item.variantId,
      quantity: item.quantity,
      price: item.price,
      title: item.title
    }));

    // Suppress the tier celebration for items the customer already had.
    this.previousDiscountRate = this.editOriginalRate;

    window.BundleEditor.renderBanner(this, result);
    window.BundleEditor.trackSession(this);

    const count = this.getTotalQuantity();
    this.announce(
      `Your bundle of ${count} item${count === 1 ? '' : 's'} has been loaded for editing. ` +
      'Your cart keeps it until you save.'
    );
  }

  /**
   * Drop a prefill parameter without acting on it, so a stale link does not sit
   * in the address bar implying something happened.
   */
  clearPrefillParam() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('prefill')) return;

    params.delete('prefill');
    const cleanUrl = window.location.pathname +
      (params.toString() ? '?' + params.toString() : '') +
      window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }

  /**
   * Handle product card click
   */
  handleProductClick(card) {
    // Block adding unavailable variants
    const variantSelect = card.querySelector('.bundle-product-card__variant-select');
    const variants = JSON.parse(card.dataset.variants);
    const currentVariant = variantSelect
      ? variants.find(v => v.id === parseInt(variantSelect.value))
      : variants[0];

    if (currentVariant && !currentVariant.available) {
      this.showToast(`${card.dataset.productTitle} is currently sold out`);
      this.showShakeAnimation(card);
      return;
    }

    const productId = card.dataset.productId;
    const existingItemIndex = this.state.selectedItems.findIndex(
      item => item.productId === productId
    );

    const totalQuantity = this.getTotalQuantity();

    if (existingItemIndex > -1 && !this.config.allowDuplicates) {
      this.removeItem(existingItemIndex);
    } else if (totalQuantity < this.getMaxCap()) {
      if (this.config.allowDuplicates && existingItemIndex > -1) {
        this.state.selectedItems[existingItemIndex].quantity += 1;
        this.render();
        this.showToast(`Added another ${this.state.selectedItems[existingItemIndex].title}`);
      } else {
        this.addItem(card);
      }
    } else {
      this.showShakeAnimation(card);
      this.showToast(`Bundle is full (${this.getMaxCap()} items max)`);
    }
  }

  /**
   * Add item to bundle
   */
  addItem(card) {
    const variantSelect = card.querySelector('.bundle-product-card__variant-select');
    const variants = JSON.parse(card.dataset.variants);
    const selectedVariant = variantSelect
      ? variants.find(v => v.id === parseInt(variantSelect.value))
      : variants[0];

    const item = {
      id: this.generateId(),
      productId: card.dataset.productId,
      productHandle: card.dataset.productHandle,
      variantId: selectedVariant.id.toString(),
      title: card.dataset.productTitle,
      variantTitle: selectedVariant.title !== 'Default Title' ? selectedVariant.title : null,
      price: selectedVariant.price,
      image: card.dataset.productImage,
      category: card.dataset.productCategory || null,
      available: selectedVariant.available,
      quantity: 1
    };

    this.state.selectedItems.push(item);
    this.render();

    if (navigator.vibrate) {
      navigator.vibrate(10);
    }

    this.track('bundle_item_added', {
      item_title: item.title,
      item_count: this.getTotalQuantity()
    });

    this.announce(`${item.title} added to bundle`);
  }

  /**
   * Lightweight analytics helper — pushes funnel events to GA4 via dataLayer
   * and gtag (both guarded), so we can see where shoppers drop off in the
   * builder. Never throws.
   */
  track(eventName, params) {
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(Object.assign({ event: eventName }, params || {}));
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params || {});
      }
    } catch (e) {
      /* analytics must never break the builder */
    }
  }

  /**
   * Remove item from bundle by index
   */
  removeItem(index) {
    const removed = this.state.selectedItems.splice(index, 1)[0];
    this.render();
    this.announce(`${removed.title} removed from bundle`);
  }

  /**
   * Remove all instances of a product from bundle
   */
  removeProductFromBundle(productId) {
    const index = this.state.selectedItems.findIndex(item => item.productId === productId);
    if (index > -1) {
      this.removeItem(index);
    }
  }

  /**
   * Handle quantity change for a product
   */
  handleQuantityChange(card, delta) {
    const productId = card.dataset.productId;
    const itemIndex = this.state.selectedItems.findIndex(item => item.productId === productId);

    if (itemIndex === -1) return;

    const item = this.state.selectedItems[itemIndex];
    const newQuantity = item.quantity + delta;
    const totalOtherItems = this.getTotalQuantity() - item.quantity;

    if (newQuantity <= 0) {
      this.removeItem(itemIndex);
    } else if (totalOtherItems + newQuantity <= this.getMaxCap()) {
      item.quantity = newQuantity;
      this.render();
    } else {
      this.showToast(`Can't add more - bundle is full`);
    }
  }

  /**
   * Handle variant selection change
   */
  handleVariantChange(card, event) {
    const productId = card.dataset.productId;
    const existingIndex = this.state.selectedItems.findIndex(
      item => item.productId === productId
    );

    if (existingIndex > -1) {
      const variants = JSON.parse(card.dataset.variants);
      const newVariant = variants.find(v => v.id === parseInt(event.target.value));

      if (newVariant) {
        this.state.selectedItems[existingIndex].variantId = newVariant.id.toString();
        this.state.selectedItems[existingIndex].variantTitle =
          newVariant.title !== 'Default Title' ? newVariant.title : null;
        this.state.selectedItems[existingIndex].price = newVariant.price;
        this.state.selectedItems[existingIndex].available = newVariant.available;
        this.render();
      }
    }

    const priceDisplay = card.querySelector('.bundle-product-card__price-current');
    const selectedOption = event.target.selectedOptions[0];
    if (priceDisplay && selectedOption) {
      priceDisplay.textContent = this.formatMoney(parseInt(selectedOption.dataset.price));
    }

    // Update button disabled state based on new variant availability
    const variants = JSON.parse(card.dataset.variants);
    const newVariant = variants.find(v => v.id === parseInt(event.target.value));
    const btn = card.querySelector('.bundle-product-card__add-btn');
    if (btn && newVariant) {
      btn.disabled = !newVariant.available;
      card.classList.toggle('is-unavailable', !newVariant.available);
    }
  }

  /**
   * Handle bundle size change
   */
  handleBundleSizeChange(option) {
    const newSize = parseInt(option.dataset.size);

    this.elements.bundleSizeOptions.forEach(opt => {
      opt.classList.remove('is-active');
      opt.setAttribute('aria-pressed', 'false');
    });
    option.classList.add('is-active');
    option.setAttribute('aria-pressed', 'true');

    this.state.currentBundleSize = newSize;

    while (this.getTotalQuantity() > newSize) {
      const lastItem = this.state.selectedItems[this.state.selectedItems.length - 1];
      if (lastItem.quantity > 1) {
        lastItem.quantity -= 1;
      } else {
        this.state.selectedItems.pop();
      }
    }

    // Update max display in progress bar
    if (this.elements.maxItemsDisplay) {
      this.elements.maxItemsDisplay.textContent = newSize;
    }

    // Update bundle max display in sidebar header
    if (this.elements.bundleMaxDisplay) {
      this.elements.bundleMaxDisplay.textContent = newSize;
    }

    // Update progress indicator markers
    this.updateProgressMarkers(newSize);

    this.render();
    this.announce(`Bundle size changed to ${newSize} items`);
  }

  /**
   * Apply a pre-built starter bundle: set the size, replace the current
   * selection with the preset's products, then scroll to the builder so the
   * customer can tweak before adding to cart.
   */
  applyPreset(products, size) {
    if (!products || !products.length) return;

    // Clear the current selection first so the size change doesn't trim anything.
    this.state.selectedItems = [];

    // Match the bundle size to the preset (updates the size selector + markers).
    const sizeOption = Array.from(this.elements.bundleSizeOptions || [])
      .find(opt => parseInt(opt.dataset.size) === size);
    if (sizeOption) {
      this.handleBundleSizeChange(sizeOption);
    } else {
      this.state.currentBundleSize = size;
    }

    // Seed the builder with the preset's products.
    products.forEach(p => {
      this.state.selectedItems.push({
        id: this.generateId(),
        productId: p.productId ? p.productId.toString() : null,
        productHandle: p.handle || null,
        variantId: p.variantId.toString(),
        title: p.title,
        variantTitle: null,
        price: p.price,
        image: p.image || null,
        category: p.category || null,
        available: true,
        // A preset may ask for several of the same product, e.g. three boxes of
        // laundry powder. The picker only holds distinct products, so the count
        // travels alongside rather than by repeating the entry.
        quantity: parseInt(p.quantity, 10) > 0 ? parseInt(p.quantity, 10) : 1
      });
    });

    // Avoid a celebration pop just from loading a preset.
    this.previousDiscountRate = this.calculateTotals().discountRate;
    this.render();

    this.track('bundle_preset_applied', { preset_size: size, item_count: this.getTotalQuantity() });
    this.scrollToProducts();
    this.showToast('Bundle loaded! Tweak it or add to cart.');
  }

  /**
   * Update progress indicator markers for new bundle size
   */
  updateProgressMarkers(newSize) {
    const markersContainer = this.container.querySelector('.bundle-progress-indicator__markers');
    if (!markersContainer) return;

    // Clear existing markers
    markersContainer.innerHTML = '';

    // Create new markers for the current bundle size
    for (let i = 1; i <= newSize; i++) {
      const marker = document.createElement('span');
      marker.className = 'bundle-progress-indicator__marker';
      marker.dataset.position = i;

      // Add discount info if there's a discount at this tier
      const discount = this.config.discountTiers[i.toString()] || 0;
      if (discount > 0) {
        marker.classList.add('has-discount');
        marker.dataset.discount = discount;
      }

      markersContainer.appendChild(marker);
    }
  }

  /**
   * Handle frequency change (one-time vs subscription)
   */
  handleFrequencyChange(event) {
    this.state.purchaseType = event.target.value;

    document.querySelectorAll('.bundle-sidebar__frequency-option').forEach(opt => {
      opt.classList.remove('is-active');
    });
    event.target.closest('.bundle-sidebar__frequency-option').classList.add('is-active');

    this.render();
  }

  /**
   * Get total quantity across all items
   */
  getTotalQuantity() {
    return this.state.selectedItems.reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Dynamic mode: how many slots to show = the smallest tier boundary that is
   * >= the current count (min = first tier, capped at the last tier).
   * e.g. boundaries [2,4,6]: 0-2 -> 2, 3-4 -> 4, 5-6 -> 6.
   */
  getTargetSize(count) {
    const b = this.config.tierBoundaries;
    if (!b.length) return this.state.currentBundleSize;
    for (let i = 0; i < b.length; i++) {
      if (count <= b[i]) return b[i];
    }
    return b[b.length - 1];
  }

  /**
   * Dynamic mode: discount % floored to the last unlocked tier (the largest
   * boundary <= count). e.g. 3 items -> the 2-item tier, 5 -> the 4-item tier.
   */
  getFlooredTierPct(count) {
    const b = this.config.tierBoundaries;
    let pct = 0;
    for (let i = 0; i < b.length; i++) {
      if (count >= b[i]) pct = this.config.discountTiers[b[i].toString()] || 0;
    }
    return pct;
  }

  /** Dynamic mode: is the count exactly on a tier boundary (checkout-eligible)? */
  isAtTier(count) {
    return this.config.tierBoundaries.indexOf(count) !== -1;
  }

  /**
   * The hard ceiling on how many items can be added. In dynamic mode this is the
   * top tier (e.g. 6) so the bundle can grow past the current tier; otherwise the
   * fixed selected size.
   */
  getMaxCap() {
    if (!this.dynamicMode) return this.state.currentBundleSize;
    const b = this.config.tierBoundaries;
    const last = b[b.length - 1];
    return this.config.maxItems ? Math.min(this.config.maxItems, last) : last;
  }

  /**
   * Calculate totals and discount
   */
  calculateTotals() {
    const itemCount = this.getTotalQuantity();
    const subtotal = this.state.selectedItems.reduce(
      (sum, item) => sum + (item.price * item.quantity), 0
    );

    // Dynamic mode: the target size follows the item count; the discount floors
    // to the last unlocked tier. Otherwise use the fixed size + exact-count tier.
    if (this.dynamicMode) {
      this.state.currentBundleSize = this.getTargetSize(itemCount);
    }
    const discountRate = this.dynamicMode
      ? this.getFlooredTierPct(itemCount) / 100
      : (this.config.discountTiers[itemCount.toString()] || 0) / 100;
    // Shopify's function applies the bundle percentage to each cart LINE and
    // rounds there, so modelling it as a single rounding on the basket total
    // drifts. On a six item bundle at 15% off $80.70 that is 2 cents, and it
    // drifted the wrong way: the builder promised $68.59 and checkout charged
    // $68.61.
    //
    // Per UNIT, floored, then multiplied by the quantity. Confirmed against
    // real orders rather than assumed: on #8422, four Dishwasher Tabs at $24.95
    // with 10% off were allocated $9.96, which is floor(2495 * 0.10) = 249 four
    // times. Flooring the line instead gives floor(9980 * 0.10) = 998, and
    // rounding the basket total gives the same, so both were a cent out on any
    // line carrying a quantity above one. Verified again on #8419 and #8411.
    const bundleDiscountAmount = this.state.selectedItems.reduce(
      (sum, item) => sum + (Math.floor(item.price * discountRate) * item.quantity),
      0
    );

    // Calculate one-time total (with bundle discount only)
    const oneTimeTotal = subtotal - bundleDiscountAmount;

    // Always calculate subscription price for display (bundle discount + subscription discount)
    const subscriptionDiscountRate = this.config.subscriptionDiscount / 100;
    const subscriptionDiscountAmount = Math.round(oneTimeTotal * subscriptionDiscountRate);
    const subscriptionTotal = oneTimeTotal - subscriptionDiscountAmount;

    // Determine actual total based on current selection
    const isSubscription = this.state.purchaseType === 'subscription';
    const actualSubscriptionDiscount = isSubscription ? subscriptionDiscountAmount : 0;
    const totalDiscount = bundleDiscountAmount + actualSubscriptionDiscount;
    const total = isSubscription ? subscriptionTotal : oneTimeTotal;

    const itemsRemaining = this.state.currentBundleSize - itemCount;

    return {
      itemCount,
      subtotal,
      discountRate,
      bundleDiscountAmount,
      subscriptionDiscountAmount,
      totalDiscount,
      total,
      oneTimeTotal,
      subscriptionTotal,
      itemsRemaining,
      isComplete: this.dynamicMode ? this.isAtTier(itemCount) : itemCount >= this.state.currentBundleSize,
      isValid: this.dynamicMode ? this.isAtTier(itemCount) : itemCount >= this.config.minItems,
      canAddMore: itemCount < this.state.currentBundleSize
    };
  }

  /**
   * Render the UI
   */
  render() {
    const totals = this.calculateTotals();

    // Check if discount tier has increased (celebration!)
    if (totals.discountRate > this.previousDiscountRate && totals.itemCount > 0) {
      this.showDiscountCelebration(Math.round(totals.discountRate * 100));
    }
    this.previousDiscountRate = totals.discountRate;

    // Update count displays
    if (this.elements.countDisplay) {
      this.elements.countDisplay.textContent = totals.itemCount;
    }
    if (this.elements.productsCountDisplay) {
      this.elements.productsCountDisplay.textContent = totals.itemCount;
    }

    // Update progress indicator
    this.updateProgressIndicator(totals);

    // Update product card states
    this.updateProductCards();

    // Update visual items grid
    this.renderItemsGrid(totals);

    // Update selected items list
    this.renderSelectedItems();

    // Update price displays
    this.updatePriceDisplays(totals);

    // Update frequency option prices
    if (this.elements.oneTimePrice) {
      this.elements.oneTimePrice.textContent = this.formatMoney(totals.oneTimeTotal);
    }
    // Show original price as compare for one-time if there's a bundle discount
    if (this.elements.oneTimeCompare) {
      if (totals.bundleDiscountAmount > 0) {
        this.elements.oneTimeCompare.textContent = this.formatMoney(totals.subtotal);
        this.elements.oneTimeCompare.style.display = '';
      } else {
        this.elements.oneTimeCompare.style.display = 'none';
      }
    }
    if (this.elements.subscriptionPrice) {
      this.elements.subscriptionPrice.textContent = this.formatMoney(totals.subscriptionTotal);
    }
    // Show one-time price as compare for subscription
    if (this.elements.subscriptionCompare && totals.oneTimeTotal > 0) {
      this.elements.subscriptionCompare.textContent = this.formatMoney(totals.oneTimeTotal);
    }

    // Update tier indicators
    this.updateTierIndicators(totals.itemCount);

    // The update flow owns its own button state. The create chain below is
    // reached only when this.editing is null and is unchanged from what it was.
    if (this.editing && window.BundleEditor) {
      window.BundleEditor.applyButtonState(this, totals);
      this.renderLockMessage();
      this.updateCategoryTracker();
      return;
    }

    // Update add to cart button
    if (this.elements.addToCartBtn) {
      if (this.state.hasBundleInCart) {
        this.elements.addToCartBtn.disabled = true;
        this.elements.addToCartBtn.classList.add('bundle-sidebar__cta--locked');
      } else {
        this.elements.addToCartBtn.disabled = !totals.isValid || this.state.isAddingToCart;
        this.elements.addToCartBtn.classList.remove('bundle-sidebar__cta--locked');
      }
    }

    if (this.elements.submitBtnText) {
      // Show the running total on the button when the bundle is checkout-ready
      // (label left, price right). Struck original keeps our savings visible.
      const readyToAdd = !this.dynamicMode || totals.isComplete;
      const showPrice = !this.state.hasBundleInCart && totals.itemCount > 0 && readyToAdd;
      if (this.state.hasBundleInCart) {
        // Say which lock this is when we know. Falls back to the original
        // wording for the plain one-bundle-at-a-time case, which is the only
        // one the create flow can reach.
        const lockLabel = window.BundleEditor
          ? window.BundleEditor.lockLabel(this.editResult, this.container)
          : null;
        this.elements.submitBtnText.textContent =
          lockLabel || 'Your Llama already has this bundle!';
      } else if (this.dynamicMode && !totals.isComplete) {
        // Between tiers: prompt for the items needed to reach the next box count.
        const remaining = Math.max(1, this.state.currentBundleSize - totals.itemCount);
        const word = remaining === 1 ? 'item' : 'items';
        this.elements.submitBtnText.textContent = totals.itemCount === 0
          ? `Add ${this.state.currentBundleSize} items to start`
          : `Add ${remaining} more ${word}`;
      } else if (totals.itemCount > 0) {
        this.elements.submitBtnText.textContent = 'Add to Cart';
      } else {
        this.elements.submitBtnText.textContent = 'Add Bundle to Cart';
      }

      if (this.elements.submitBtnPriceNow) {
        this.elements.submitBtnPriceNow.textContent = this.formatMoney(totals.oneTimeTotal);
      }
      if (this.elements.submitBtnPriceWas) {
        if (totals.bundleDiscountAmount > 0) {
          this.elements.submitBtnPriceWas.textContent = this.formatMoney(totals.subtotal);
          this.elements.submitBtnPriceWas.style.display = '';
        } else {
          this.elements.submitBtnPriceWas.style.display = 'none';
        }
      }
      if (this.elements.addToCartBtn) {
        this.elements.addToCartBtn.classList.toggle('is-priced', showPrice);
      }
    }

    // Show/hide bundle-in-cart lock message
    this.renderLockMessage();

    // Update category tracker
    this.updateCategoryTracker();
  }

  /**
   * Inject or update the lock message below the ATC button when a bundle is already in the cart.
   */
  renderLockMessage() {
    const actions = this.elements.addToCartBtn?.parentElement;
    if (!actions) return;

    let msg = actions.querySelector('.bundle-sidebar__lock-msg');

    if (this.state.hasBundleInCart) {
      if (!msg) {
        msg = document.createElement('div');
        msg.className = 'bundle-sidebar__lock-msg';
        // In the create flow editResult is null, so this is the same sentence
        // it has always been. It only changes when we locked for a specific
        // reason the customer deserves to hear.
        const reason = window.BundleEditor
          ? window.BundleEditor.lockReason(this.editResult, this.container)
          : null;
        msg.textContent = reason ||
          'Only one bundle can be active at a time. To start a new one, remove the current bundle from your cart first.';
        actions.appendChild(msg);
      }
    } else if (msg) {
      msg.remove();
    }
  }

  /**
   * Update progress indicator with current marker at fill point
   */
  updateProgressIndicator(totals) {
    // Dynamic mode: the target size can change on every add/remove — keep the
    // "/max" displays and the progress markers in sync as boxes reveal/collapse.
    if (this.dynamicMode && this._renderedSize !== this.state.currentBundleSize) {
      if (this.elements.bundleMaxDisplay) {
        this.elements.bundleMaxDisplay.textContent = this.state.currentBundleSize;
      }
      if (this.elements.maxItemsDisplay) {
        this.elements.maxItemsDisplay.textContent = this.state.currentBundleSize;
      }
      this.updateProgressMarkers(this.state.currentBundleSize);
      this._renderedSize = this.state.currentBundleSize;
    }

    const progressPercent = (totals.itemCount / this.state.currentBundleSize) * 100;

    if (this.elements.progressFill) {
      this.elements.progressFill.style.width = `${progressPercent}%`;
    }

    // Show/hide current marker based on count
    if (this.elements.currentMarker) {
      if (totals.itemCount > 0) {
        this.elements.currentMarker.style.display = 'flex';
      } else {
        this.elements.currentMarker.style.display = 'none';
      }
    }

    // Update progress markers
    this.container.querySelectorAll('.bundle-progress-indicator__marker').forEach(marker => {
      const position = parseInt(marker.dataset.position);
      marker.classList.toggle('is-filled', position <= totals.itemCount);
    });

    // Toggle complete state on progress indicator
    if (this.elements.progressIndicator) {
      this.elements.progressIndicator.classList.toggle('is-complete', totals.isComplete);
    }

    // Show/hide max display based on completion
    if (this.elements.maxItemsDisplay) {
      this.elements.maxItemsDisplay.style.display = totals.isComplete ? 'none' : '';
    }
  }

  /**
   * Update tier indicators in sidebar
   */
  updateTierIndicators(itemCount) {
    this.elements.tierItems?.forEach(tier => {
      const tierNum = parseInt(tier.dataset.tier);
      tier.classList.toggle('is-active', itemCount >= tierNum);
    });
  }

  /**
   * Show discount celebration banner
   */
  showDiscountCelebration(discountPercent) {
    // Funnel: a higher discount tier was reached
    this.track('bundle_tier_unlocked', { discount_percent: discountPercent });

    if (!this.elements.discountCelebration) return;

    // Update celebration text
    if (this.elements.celebrationText) {
      this.elements.celebrationText.textContent = `${discountPercent}% Discount Unlocked!`;
    }

    // Show the celebration banner
    this.elements.discountCelebration.style.display = 'flex';

    // Re-trigger animation
    this.elements.discountCelebration.style.animation = 'none';
    this.elements.discountCelebration.offsetHeight; // Trigger reflow
    this.elements.discountCelebration.style.animation = '';

    // Haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate([50, 50, 100]);
    }

    // Hide after 3 seconds
    clearTimeout(this.celebrationTimeout);
    this.celebrationTimeout = setTimeout(() => {
      if (this.elements.discountCelebration) {
        this.elements.discountCelebration.style.display = 'none';
      }
    }, 3000);

    // Announce for screen readers
    this.announce(`${discountPercent}% discount unlocked!`);
  }

  /**
   * Update product card states
   */
  updateProductCards() {
    this.elements.productCards.forEach(card => {
      const productId = card.dataset.productId;
      const item = this.state.selectedItems.find(item => item.productId === productId);
      const isSelected = !!item;

      card.classList.toggle('is-selected', isSelected);

      const btn = card.querySelector('.bundle-product-card__add-btn');
      if (btn) {
        btn.setAttribute('aria-pressed', isSelected.toString());
      }

      const qtyControls = card.querySelector('.bundle-product-card__quantity-controls');
      if (qtyControls) {
        qtyControls.style.display = isSelected && this.config.allowDuplicates ? 'flex' : 'none';

        const qtyValue = card.querySelector('.bundle-product-card__qty-value');
        if (qtyValue && item) {
          qtyValue.textContent = item.quantity;
        }
      }

      const qtyBadge = card.querySelector('.bundle-product-card__quantity-badge');
      if (qtyBadge && this.config.allowDuplicates) {
        if (isSelected && item.quantity > 1) {
          qtyBadge.style.display = 'flex';
          const countEl = qtyBadge.querySelector('.bundle-product-card__quantity-badge-count');
          if (countEl) countEl.textContent = item.quantity;
        } else {
          qtyBadge.style.display = 'none';
        }
      }

      const removeBtn = card.querySelector('.bundle-product-card__quick-remove');
      if (removeBtn) {
        removeBtn.style.display = isSelected ? 'flex' : 'none';
      }

      // Sync button disabled state with current variant availability
      const variantSelect = card.querySelector('.bundle-product-card__variant-select');
      const variants = JSON.parse(card.dataset.variants);
      const currentVariant = variantSelect
        ? variants.find(v => v.id === parseInt(variantSelect.value))
        : variants[0];
      if (btn && currentVariant) {
        btn.disabled = !currentVariant.available;
        card.classList.toggle('is-unavailable', !currentVariant.available);
      }
    });
  }

  /**
   * Render selected items list
   */
  renderSelectedItems() {
    if (!this.elements.selectedItemsContainer) return;

    if (this.state.selectedItems.length === 0) {
      this.elements.selectedItemsContainer.innerHTML = '';
      return;
    }

    const itemsHtml = this.state.selectedItems.map((item, index) => `
      <div class="bundle-sidebar__item" data-item-id="${item.id}">
        <div class="bundle-sidebar__item-image-wrapper">
          <img src="${item.image}"
               alt="${this.escapeHtml(item.title)}"
               class="bundle-sidebar__item-image"
               width="44"
               height="44"
               loading="lazy">
          ${item.quantity > 1 ? `<span class="bundle-sidebar__item-qty-badge">${item.quantity}</span>` : ''}
        </div>
        <div class="bundle-sidebar__item-info">
          <span class="bundle-sidebar__item-title">${this.escapeHtml(item.title)}</span>
          ${item.variantTitle ? `<span class="bundle-sidebar__item-variant">${this.escapeHtml(item.variantTitle)}</span>` : ''}
        </div>
        <span class="bundle-sidebar__item-price">${this.formatMoney(item.price * item.quantity)}</span>
        <button type="button"
                class="bundle-sidebar__item-remove"
                aria-label="Remove ${this.escapeHtml(item.title)}"
                data-index="${index}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        </button>
      </div>
    `).join('');

    this.elements.selectedItemsContainer.innerHTML = itemsHtml;

    this.elements.selectedItemsContainer.querySelectorAll('.bundle-sidebar__item-remove')
      .forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeItem(parseInt(btn.dataset.index));
        });
      });
  }

  /**
   * Render visual items grid with thumbnails and empty slots
   */
  renderItemsGrid(totals) {
    if (!this.elements.itemsGrid) return;

    const slots = this.elements.itemsGrid.querySelectorAll('.bundle-sidebar__slot');
    const maxItems = this.state.currentBundleSize;

    // Build list of items expanded by quantity
    const expandedItems = [];
    this.state.selectedItems.forEach((item, itemIndex) => {
      for (let q = 0; q < item.quantity; q++) {
        expandedItems.push({ ...item, itemIndex, qtyIndex: q });
      }
    });

    slots.forEach((slot, index) => {
      const slotNum = index + 1;

      // Hide slots beyond current bundle size
      if (slotNum > maxItems) {
        slot.style.display = 'none';
        return;
      }
      slot.style.display = '';

      // Check if we have an item for this slot
      if (index < expandedItems.length) {
        const item = expandedItems[index];
        slot.classList.remove('bundle-sidebar__slot--empty');
        slot.classList.add('bundle-sidebar__slot--filled');

        slot.innerHTML = `
          <div class="bundle-sidebar__slot-inner">
            <img src="${item.image}" alt="${this.escapeHtml(item.title)}" class="bundle-sidebar__slot-image">
            <button type="button" class="bundle-sidebar__slot-remove" data-item-index="${item.itemIndex}" aria-label="Remove ${this.escapeHtml(item.title)}">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        `;

        // Add remove event listener
        const removeBtn = slot.querySelector('.bundle-sidebar__slot-remove');
        removeBtn?.addEventListener('click', (e) => {
          e.stopPropagation();
          const itemIndex = parseInt(removeBtn.dataset.itemIndex);
          const item = this.state.selectedItems[itemIndex];
          if (item && item.quantity > 1) {
            item.quantity -= 1;
            this.render();
          } else {
            this.removeItem(itemIndex);
          }
        });
      } else {
        // Empty slot
        slot.classList.add('bundle-sidebar__slot--empty');
        slot.classList.remove('bundle-sidebar__slot--filled');
        slot.innerHTML = `
          <div class="bundle-sidebar__slot-inner">
            <span class="bundle-sidebar__slot-plus">+</span>
          </div>
        `;

        // Click on empty slot scrolls to products
        slot.addEventListener('click', () => this.scrollToProducts(), { once: true });
      }
    });

    // Update Show All toggle visibility
    if (this.elements.showToggle) {
      // Show toggle only if we have more than 4 items
      this.elements.showToggle.style.display = maxItems > 4 ? '' : 'none';
      const toggleText = this.elements.showToggle.querySelector('.bundle-sidebar__show-toggle-text');
      const toggleIcon = this.elements.showToggle.querySelector('.bundle-sidebar__show-toggle-icon');
      if (toggleText) {
        toggleText.textContent = this.gridExpanded ? 'Show Less' : 'Show All';
      }
      if (toggleIcon) {
        toggleIcon.style.transform = this.gridExpanded ? 'rotate(180deg)' : '';
      }
    }

    // Update Add More button. In dynamic mode the disabled submit button already
    // carries the "Add N more" prompt, so this secondary helper is hidden.
    if (this.elements.addMoreBtn && this.elements.addMoreText) {
      if (!this.dynamicMode && totals.itemsRemaining > 0 && totals.itemCount > 0) {
        this.elements.addMoreBtn.style.display = '';
        const addMoreText = this.config.addMoreText.replace('{count}', totals.itemsRemaining);
        this.elements.addMoreText.textContent = addMoreText;
      } else {
        this.elements.addMoreBtn.style.display = 'none';
      }
    }

    // Manage grid collapsed/expanded state
    if (this.elements.itemsGrid) {
      this.elements.itemsGrid.classList.toggle('is-expanded', this.gridExpanded);
    }
  }

  /**
   * Toggle grid expand/collapse
   */
  toggleGridExpand() {
    this.gridExpanded = !this.gridExpanded;
    this.render();
  }

  /**
   * Scroll to products section
   */
  scrollToProducts() {
    const productsSection = this.container.querySelector('.bundle-products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Update price displays
   */
  updatePriceDisplays(totals) {
    if (this.elements.subtotalDisplay) {
      this.elements.subtotalDisplay.textContent = this.formatMoney(totals.subtotal);
    }

    if (this.elements.discountDisplay) {
      this.elements.discountDisplay.textContent = `-${this.formatMoney(totals.bundleDiscountAmount)}`;
    }

    if (this.elements.discountRow) {
      this.elements.discountRow.style.display = totals.bundleDiscountAmount > 0 ? 'flex' : 'none';
    }

    if (this.elements.subscriptionDiscountDisplay) {
      this.elements.subscriptionDiscountDisplay.textContent =
        `-${this.formatMoney(totals.subscriptionDiscountAmount)}`;
    }

    if (this.elements.subscriptionRow) {
      this.elements.subscriptionRow.style.display = totals.subscriptionDiscountAmount > 0 ? 'flex' : 'none';
    }

    if (this.elements.totalDisplay) {
      this.elements.totalDisplay.textContent = this.formatMoney(totals.total);
    }

    // Persistent savings summary + next-tier nudge
    this.updateSavings(totals);
  }

  /**
   * Update the persistent savings summary ("You save $X (Y% off)") and the
   * next-tier nudge ("Add N more to save Z%"). Uses figures already computed
   * in calculateTotals(); no server data needed.
   */
  updateSavings(totals) {
    const el = this.elements;
    if (!el.savingsBlock) return;

    // Hide the whole block until at least one item is chosen.
    if (totals.itemCount === 0) {
      el.savingsBlock.style.display = 'none';
      return;
    }
    el.savingsBlock.style.display = '';

    // Current (discounted) total.
    if (el.savingsNow) {
      el.savingsNow.textContent = this.formatMoney(totals.total);
    }

    // Original price, struck through — only when something is actually saved.
    const saved = totals.totalDiscount;
    if (el.savingsOriginal) {
      if (saved > 0) {
        el.savingsOriginal.textContent = this.formatMoney(totals.subtotal);
        el.savingsOriginal.style.display = '';
      } else {
        el.savingsOriginal.style.display = 'none';
      }
    }

    // The headline: the actual money saved + the effective %.
    if (el.savingsBadge) {
      if (saved > 0) {
        const pct = Math.round((saved / totals.subtotal) * 100);
        el.savingsBadge.textContent = `You save ${this.formatMoney(saved)} (${pct}% off)`;
        el.savingsBadge.style.display = '';
      } else {
        el.savingsBadge.style.display = 'none';
      }
    }

    // Next-tier nudge: the next item count that unlocks a higher discount.
    if (el.savingsNext) {
      const next = this.getNextTier(totals);
      if (next) {
        const word = next.needed === 1 ? 'item' : 'items';
        el.savingsNext.textContent = `Add ${next.needed} more ${word} to save ${next.percent}%`;
        el.savingsNext.style.display = '';
      } else {
        el.savingsNext.style.display = 'none';
      }
    }
  }

  /**
   * Find the next item count (within max) that unlocks a higher discount rate
   * than the current one. Returns { needed, percent } or null if already at top.
   */
  getNextTier(totals) {
    const tiers = this.config.discountTiers || {};
    const max = this.config.maxItems || 10;
    for (let c = totals.itemCount + 1; c <= max; c++) {
      const rate = (tiers[c.toString()] || 0) / 100;
      if (rate > totals.discountRate) {
        return { needed: c - totals.itemCount, percent: Math.round(rate * 100) };
      }
    }
    return null;
  }

  /**
   * Update category tracker
   */
  updateCategoryTracker() {
    if (!this.config.showCategoryTracker || !this.elements.categoryTracker) return;

    const categories = {};
    this.state.selectedItems.forEach(item => {
      if (item.category) {
        categories[item.category] = (categories[item.category] || 0) + item.quantity;
      }
    });

    if (Object.keys(categories).length === 0) {
      this.elements.categoryTracker.style.display = 'none';
      return;
    }

    this.elements.categoryTracker.style.display = 'block';

    const categoriesHtml = Object.entries(categories).map(([cat, count]) => `
      <div class="bundle-category-tracker__category">
        <span class="bundle-category-tracker__category-icon">
          <span class="bundle-category-tracker__category-count">${count}</span>
        </span>
        <span class="bundle-category-tracker__category-name">${this.escapeHtml(cat)}</span>
      </div>
    `).join('');

    if (this.elements.categoryList) {
      this.elements.categoryList.innerHTML = categoriesHtml;
    }
  }

  /**
   * Handle add to cart
   */
  async handleAddToCart() {
    if (this.state.selectedItems.length === 0 || this.state.isAddingToCart) return;

    // Update flow saves by replacing the bundle already in the cart, not by
    // adding a second one. Everything below this line is the create path.
    if (this.editing && window.BundleEditor) {
      return window.BundleEditor.save(this);
    }

    // Dynamic mode: only a count sitting exactly on a tier (2/4/6) may be added.
    if (this.dynamicMode && !this.isAtTier(this.getTotalQuantity())) {
      const remaining = Math.max(1, this.state.currentBundleSize - this.getTotalQuantity());
      this.showToast(`Add ${remaining} more item${remaining === 1 ? '' : 's'} to complete your bundle.`);
      return;
    }

    // Block if a bundle is already in the cart
    if (this.state.hasBundleInCart) {
      this.showToast('You already have a bundle in your cart. Remove it first to add a new one.');
      return;
    }

    // Check for unavailable items before submitting
    const unavailable = this.state.selectedItems.filter(item => item.available === false);
    if (unavailable.length > 0) {
      const names = [...new Set(unavailable.map(i => i.title))].join(', ');
      this.showToast(`Please remove sold-out items before checkout: ${names}`);
      return;
    }

    this.state.isAddingToCart = true;
    this.elements.addToCartBtn?.classList.add('is-loading');
    this.render();

    const bundleId = this.generateBundleId();
    const totals = this.calculateTotals();

    // Consolidate duplicate variants so the same product appears once with higher quantity
    const consolidatedMap = new Map();
    this.state.selectedItems.forEach(item => {
      const key = item.variantId.toString();
      if (consolidatedMap.has(key)) {
        consolidatedMap.get(key).quantity += item.quantity;
      } else {
        consolidatedMap.set(key, { ...item, quantity: item.quantity });
      }
    });

    const items = [];
    let position = 1;

    consolidatedMap.forEach(item => {
      items.push({
        id: parseInt(item.variantId),
        quantity: item.quantity,
        properties: {
          '_bundle_id': bundleId,
          '_bundle_position': position.toString(),
          '_bundle_total_items': totals.itemCount.toString(),
          '_bundle_discount_tier': Math.round(totals.discountRate * 100).toString(),
          '_bundle_original_price': item.price.toString(),
          // Per-unit price after the discount, computed the same way Shopify
          // allocates it: floor the per-unit discount. Keeps the cart's item
          // rows summing to the bundle total instead of drifting a cent.
          '_bundle_discounted_price': (item.price - Math.floor(item.price * totals.discountRate)).toString(),
          '_bundle_purchase_type': this.state.purchaseType
        }
      });
      position++;
    });

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const shopifyMessage = errorData?.description || errorData?.message || '';
        throw new Error(shopifyMessage || 'Failed to add to cart');
      }

      const result = await response.json();
      console.log('Bundle added to cart:', result);

      // Funnel: bundle added to cart (with value + savings)
      const addTotals = this.calculateTotals();
      this.track('bundle_add_to_cart', {
        item_count: addTotals.itemCount,
        value: addTotals.total / 100,
        bundle_savings: addTotals.totalDiscount / 100,
        discount_percent: Math.round(addTotals.discountRate * 100),
        currency: this.config.currency || 'USD'
      });

      this.showToast('Bundle added to cart!');

      // If items were prefilled from cart, show replace/keep modal
      // instead of redirecting immediately
      if (this.state.prefillSourceVariants && this.state.prefillSourceVariants.length > 0) {
        this.state.isAddingToCart = false;
        this.elements.addToCartBtn?.classList.remove('is-loading');
        this.showPrefillReplaceModal();
        return;
      }

      window.location.href = '/cart';

    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.showToast(error.message || 'Something went wrong. Please try again.');
    } finally {
      this.state.isAddingToCart = false;
      this.elements.addToCartBtn?.classList.remove('is-loading');
      this.render();
    }
  }

  /**
   * Show replace/keep modal after adding a prefilled bundle to cart.
   * The user's original (non-bundled) items are still in the cart;
   * this modal lets them choose to replace or keep both.
   */
  showPrefillReplaceModal() {
    // Remove any existing modal
    const existing = document.querySelector('.bundle-replace-modal');
    if (existing) existing.remove();

    const totalPrefilled = this.state.prefillSourceVariants.reduce(
      (sum, v) => sum + v.quantity, 0
    );
    const itemWord = totalPrefilled === 1 ? 'item' : 'items';

    const overlay = document.createElement('div');
    overlay.className = 'bundle-replace-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'bundle-replace-title');
    overlay.innerHTML = `
      <div class="bundle-replace-modal__backdrop"></div>
      <div class="bundle-replace-modal__content">
        <h3 id="bundle-replace-title" class="bundle-replace-modal__title">
          Bundle added to cart
        </h3>
        <p class="bundle-replace-modal__desc">
          You had ${totalPrefilled} ${itemWord} in your cart that ${totalPrefilled === 1 ? 'is' : 'are'} now
          included in your bundle (with discount). Would you like to remove
          the original ${itemWord} to avoid duplicates?
        </p>
        <div class="bundle-replace-modal__actions">
          <button type="button" class="bundle-replace-modal__btn bundle-replace-modal__btn--primary"
                  data-action="replace">
            Replace in cart
          </button>
          <button type="button" class="bundle-replace-modal__btn bundle-replace-modal__btn--secondary"
                  data-action="keep">
            Keep both
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Focus the primary button
    const primaryBtn = overlay.querySelector('[data-action="replace"]');
    primaryBtn?.focus();

    // Handle button clicks
    overlay.addEventListener('click', async (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      const isBackdrop = e.target.classList.contains('bundle-replace-modal__backdrop');

      if (action === 'replace') {
        primaryBtn.textContent = 'Removing...';
        primaryBtn.disabled = true;
        await this.removePrefillOriginals();
        overlay.remove();
        window.location.href = '/cart';
      } else if (action === 'keep' || isBackdrop) {
        overlay.remove();
        window.location.href = '/cart';
      }
    });

    // ESC key closes (same as "Keep both")
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        document.removeEventListener('keydown', handleEsc);
        overlay.remove();
        window.location.href = '/cart';
      }
    };
    document.addEventListener('keydown', handleEsc);

    this.announce(
      'Bundle added to cart. Choose to replace or keep the original items.'
    );
  }

  /**
   * Remove the original (non-bundled) cart items that were used for prefill.
   * Fetches /cart.js to get line item keys, then sets quantity to 0 for
   * matching items that do NOT have a _bundle_id property.
   */
  async removePrefillOriginals() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      });
      const cart = await res.json();

      // Build a set of prefilled variant IDs for fast lookup
      const prefillVids = new Set(
        this.state.prefillSourceVariants.map(v => v.variantId)
      );

      // Find original (non-bundled) line items that match prefilled variants
      const updates = {};
      cart.items.forEach(item => {
        const vid = item.variant_id.toString();
        const isBundleItem = item.properties && item.properties._bundle_id;
        if (prefillVids.has(vid) && !isBundleItem) {
          updates[item.key] = 0;
        }
      });

      if (Object.keys(updates).length === 0) return;

      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (updateRes.ok) {
        // Ask the theme to re-read its cart sections rather than emitting
        // cartUpdate with a plain cart object. The subscriber in assets/cart.js
        // reads event.cart.sections[sectionName], which only exists when the
        // cart was fetched with sections requested, and /cart/update.js does
        // not do that. Emitting a plain cart throws inside that subscriber, and
        // since emit is synchronous the exception propagates out of here.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      console.error('Error removing prefill originals:', error);
      // Don't block navigation — items can be removed manually from cart
    }
  }

  /**
   * Toggle mobile sidebar
   */
  toggleSidebar() {
    const isExpanded = this.elements.sidebarToggle?.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;

    this.elements.sidebarToggle?.setAttribute('aria-expanded', newState.toString());
    this.elements.sidebar?.classList.toggle('is-expanded', newState);

    // Update toggle text
    const toggleText = this.elements.sidebarToggle?.querySelector('.bundle-sidebar__expand-text');
    if (toggleText) {
      toggleText.textContent = newState ? 'Hide' : 'Details';
    }

    // Update expand hint text
    if (this.elements.expandHint) {
      this.elements.expandHint.textContent = newState ? 'Tap to collapse' : 'Tap to expand';
    }

    // Also expand/collapse the items grid on mobile
    if (this.gridExpanded !== newState) {
      this.gridExpanded = newState;
      this.render();
    }
  }

  /**
   * Show shake animation on element
   */
  showShakeAnimation(element) {
    element.style.animation = 'none';
    element.offsetHeight;
    element.style.animation = 'shake 0.4s ease';
  }

  /**
   * Format price in currency
   */
  formatMoney(cents) {
    return this.currencyFormatter.format(cents / 100);
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Parse prefill URL parameter and auto-select matching products.
   * URL format: ?prefill=variantId1,variantId2:qty,...
   * Builds items directly into state (no addItem() calls) to avoid
   * duplicate entries and intermediate renders.
   * Cleans the URL after processing.
   */
  parsePrefill() {
    const params = new URLSearchParams(window.location.search);
    const prefillParam = params.get('prefill');
    if (!prefillParam) return;

    const entries = decodeURIComponent(prefillParam).split(',').map(entry => {
      const parts = entry.trim().split(':');
      return { variantId: parts[0], quantity: parseInt(parts[1]) || 1 };
    }).filter(e => e.variantId);

    if (entries.length === 0) return;

    const totalPrefillItems = entries.reduce((sum, e) => sum + e.quantity, 0);
    this.autoSelectBundleSize(totalPrefillItems);

    // Track which variant IDs came from prefill (needed for replace modal)
    this.state.prefillSourceVariants = [];

    let currentTotal = this.getTotalQuantity();

    entries.forEach(entry => {
      if (currentTotal >= this.getMaxCap()) return;

      const card = this.findCardByVariantId(entry.variantId);
      if (!card) return;

      // Set the variant dropdown to the correct variant if multi-variant
      const variantSelect = card.querySelector('.bundle-product-card__variant-select');
      if (variantSelect) {
        variantSelect.value = entry.variantId;
      }

      // Resolve the variant data from the card
      const variants = JSON.parse(card.dataset.variants);
      const selectedVariant = variantSelect
        ? variants.find(v => v.id === parseInt(variantSelect.value))
        : variants[0];

      if (!selectedVariant) return;

      // Check if this variant already exists in selectedItems
      const existingIndex = this.state.selectedItems.findIndex(
        item => item.variantId === selectedVariant.id.toString()
      );

      // Determine how many we can actually add
      const qty = this.config.allowDuplicates
        ? Math.min(entry.quantity, this.getMaxCap() - currentTotal)
        : 1;

      if (qty <= 0) return;

      if (existingIndex > -1 && this.config.allowDuplicates) {
        // Increment existing entry's quantity
        this.state.selectedItems[existingIndex].quantity += qty;
      } else if (existingIndex === -1) {
        // Build item object directly (same shape as addItem() produces)
        this.state.selectedItems.push({
          id: this.generateId(),
          productId: card.dataset.productId,
          productHandle: card.dataset.productHandle,
          variantId: selectedVariant.id.toString(),
          title: card.dataset.productTitle,
          variantTitle: selectedVariant.title !== 'Default Title' ? selectedVariant.title : null,
          price: selectedVariant.price,
          image: card.dataset.productImage,
          category: card.dataset.productCategory || null,
          available: selectedVariant.available,
          quantity: qty
        });
      }

      currentTotal += qty;

      // Record this variant as a prefill source
      this.state.prefillSourceVariants.push({
        variantId: selectedVariant.id.toString(),
        quantity: qty
      });
    });

    // Clean URL without triggering a page reload
    params.delete('prefill');
    const cleanUrl = window.location.pathname +
      (params.toString() ? '?' + params.toString() : '') +
      window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    // Suppress celebration animation for prefilled items
    this.previousDiscountRate = this.calculateTotals().discountRate;

    // Single render after all state changes
    this.render();

    const prefillCount = this.getTotalQuantity();
    if (prefillCount > 0) {
      this.announce(
        prefillCount + ' item' + (prefillCount > 1 ? 's' : '') +
        ' from your cart have been added to the bundle builder'
      );
    }
  }

  /**
   * Find a product card DOM element that contains a given variant ID.
   */
  findCardByVariantId(variantId) {
    for (const card of this.elements.productCards) {
      const variants = JSON.parse(card.dataset.variants);
      const match = variants.find(v => v.id.toString() === variantId.toString());
      if (match) return card;
    }
    return null;
  }

  /**
   * Programmatically select the smallest bundle size >= itemCount.
   * Falls back to the largest available size.
   */
  autoSelectBundleSize(itemCount) {
    if (!this.config.showBundleSizeSelector) return;

    const sizes = Array.from(this.elements.bundleSizeOptions)
      .map(opt => ({ element: opt, size: parseInt(opt.dataset.size) }))
      .sort((a, b) => a.size - b.size);

    if (sizes.length === 0) return;

    const target = sizes.find(s => s.size >= itemCount) || sizes[sizes.length - 1];
    this.handleBundleSizeChange(target.element);
  }

  /**
   * Generate unique ID for item
   */
  generateId() {
    return `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate unique bundle ID
   */
  generateBundleId() {
    return `bnd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Show toast message
   */
  showToast(message) {
    const existing = document.querySelector('.bundle-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'bundle-toast';
    toast.textContent = message;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.classList.add('is-hiding');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  /**
   * Announce for screen readers
   */
  announce(message) {
    const announcer = document.getElementById('bundle-announcer') || this.createAnnouncer();
    announcer.textContent = message;
  }

  /**
   * Create ARIA live region for announcements
   */
  createAnnouncer() {
    const announcer = document.createElement('div');
    announcer.id = 'bundle-announcer';
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';
    document.body.appendChild(announcer);
    return announcer;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new BundleBuilder());
} else {
  new BundleBuilder();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = BundleBuilder;
}
